import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
	FiAlertTriangle,
	FiCheckCircle,
	FiChevronLeft,
	FiChevronRight,
	FiCornerUpRight,
	FiExternalLink,
	FiEyeOff,
	FiLink2,
	FiRefreshCw,
	FiRotateCcw,
	FiTool,
	FiTrash2,
	FiUploadCloud
} from "react-icons/fi";
import { Modal } from "../Components/Modal";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import GatedButton from "../Components/GatedButton";
import FixSourceModal, { FixSourceTarget } from "../Components/Redirects/FixSourceModal";
import {
	Redirect,
	RedirectTask,
	RedirectTaskCount,
	clearAllTasks,
	createRedirect,
	deleteRedirect,
	dismissTask,
	displayPath,
	editorRouteFor,
	extractErrorMessage,
	fetchRedirects,
	fetchTaskCount,
	fetchTasks,
	fixRuleLinks,
	formatTaskDate,
	parseTaskDetails,
	publicUrlFor,
	publishRedirects,
	reopenTask,
	taskKindLabel
} from "../Components/Redirects/redirectsApi";

type TabKey = "preview" | "production" | "rules";
type StatusFilter = "open" | "resolved" | "dismissed" | "all";
type Accent = "amber" | "rose";

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
	{ key: "open", label: "Ouvertes" },
	{ key: "resolved", label: "Résolues" },
	{ key: "dismissed", label: "Ignorées" },
	{ key: "all", label: "Toutes" }
];

const TASK_PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Overlay de purge au mot tapé SUPPRIMER (même patron que la purge des archives)
// ---------------------------------------------------------------------------

const PurgeConfirmOverlay: React.FC<{
	show: boolean;
	title: string;
	description: React.ReactNode;
	busy: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}> = ({ show, title, description, busy, onCancel, onConfirm }) => {
	const [confirmWord, setConfirmWord] = useState("");

	useEffect(() => {
		if (show) setConfirmWord("");
	}, [show]);

	if (!show) return null;
	return (
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 p-4">
			<div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
				<h2 className="flex items-center gap-2 text-lg font-bold text-rose-700">
					<FiTrash2 /> {title}
				</h2>
				<div className="mt-3 text-sm text-slate-700">{description}</div>
				<p className="mt-3 text-sm text-slate-600">
					Pour confirmer, saisissez <span className="font-mono font-semibold">SUPPRIMER</span> :
				</p>
				<input
					autoFocus
					className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none"
					value={confirmWord}
					onChange={(e) => setConfirmWord(e.target.value)}
					placeholder="SUPPRIMER"
				/>
				<div className="mt-5 flex justify-end gap-2">
					<button
						type="button"
						onClick={onCancel}
						disabled={busy}
						className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={confirmWord.trim() !== "SUPPRIMER" || busy}
						className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{busy ? <LoadingSpinner size="sm" inline /> : <FiTrash2 className="h-4 w-4" />}
						Supprimer définitivement
					</button>
				</div>
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Petits éléments visuels des tâches
// ---------------------------------------------------------------------------

/** Pastille de sévérité : point rouge = erreur (production), ambre = avertissement (préview). */
const SeverityDot: React.FC<{ severity?: string | null }> = ({ severity }) => (
	<span
		className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
			severity === "error" ? "bg-rose-500 ring-4 ring-rose-100" : "bg-amber-500 ring-4 ring-amber-100"
		}`}
		title={severity === "error" ? "Erreur (production)" : "Avertissement (préview)"}
		aria-hidden="true"
	/>
);

/** Étiquette de statut pour les tâches résolues/ignorées (les ouvertes n'en ont pas besoin). */
const TaskStatusPill: React.FC<{ status?: string | null }> = ({ status }) => {
	if (status === "resolved") {
		return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">Résolue</span>;
	}
	if (status === "dismissed") {
		return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Ignorée</span>;
	}
	return null;
};

// ---------------------------------------------------------------------------
// « Créer une redirection » depuis une tâche (fromPath verrouillé = URL cassée)
// ---------------------------------------------------------------------------

const CreateRedirectFromTaskModal: React.FC<{
	task: RedirectTask | null;
	onHide: () => void;
	/** Appelé après création : le backend résout la tâche, le parent rafraîchit tout. */
	onCreated: () => void;
}> = ({ task, onHide, onCreated }) => {
	const [toPath, setToPath] = useState("");
	const [isPermanent, setIsPermanent] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (task) {
			setToPath("");
			setIsPermanent(true);
			setSubmitting(false);
			setError(null);
		}
	}, [task]);

	const submit = async () => {
		if (!task) return;
		const to = toPath.trim();
		if (!to) {
			setError("Renseignez la destination de la redirection.");
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await createRedirect(task.url, to, isPermanent);
			toast.success("Redirection créée : la tâche est résolue.");
			onCreated();
			onHide();
		} catch (err) {
			console.error("Échec de la création de la redirection", err);
			// Le 400 anti-boucle porte un message français corrigeable par l'utilisateur.
			setError(extractErrorMessage(err) ?? "Impossible de créer la redirection.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal show={Boolean(task)} onHide={() => (submitting ? undefined : onHide())} size="sm" centered>
			<Modal.Header closeButton>
				<Modal.Title>Créer une redirection</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="space-y-3">
					<p className="text-sm text-slate-600">
						Les visiteurs de l'URL cassée seront redirigés. La tâche sera résolue automatiquement.
					</p>
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Depuis (verrouillé)</span>
						<input
							type="text"
							value={displayPath(task?.url)}
							readOnly
							disabled
							className="mt-1 w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 font-mono text-xs text-slate-500"
						/>
					</label>
					<label className="block">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vers</span>
						<input
							type="text"
							value={toPath}
							onChange={(e) => setToPath(e.target.value)}
							placeholder="/nouvelle-url"
							autoFocus
							className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:outline-none"
							onKeyDown={(e) => e.key === "Enter" && submit()}
						/>
					</label>
					<label className="flex items-center gap-2 text-sm text-slate-700">
						<input
							type="checkbox"
							checked={isPermanent}
							onChange={(e) => setIsPermanent(e.target.checked)}
							className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
						/>
						Permanente (301)
					</label>
					{error && (
						<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
							{error}
						</p>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					disabled={submitting}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
				>
					Annuler
				</button>
				<button
					type="button"
					onClick={submit}
					disabled={submitting || !toPath.trim()}
					className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{submitting ? <LoadingSpinner size="sm" inline /> : <FiCornerUpRight className="h-4 w-4" />}
					{submitting ? "Création…" : "Créer la redirection"}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

// ---------------------------------------------------------------------------
// Une carte de tâche
// ---------------------------------------------------------------------------

const TaskCard: React.FC<{
	task: RedirectTask;
	accent: Accent;
	mutatingId: string | null;
	onFix: (task: RedirectTask) => void;
	onCreateRedirect: (task: RedirectTask) => void;
	onDismiss: (task: RedirectTask) => void;
	onReopen: (task: RedirectTask) => void;
}> = ({ task, accent, mutatingId, onFix, onCreateRedirect, onDismiss, onReopen }) => {
	const details = parseTaskDetails(task.detailsJson);
	const isOpen = task.status === "open";
	const isMutating = mutatingId === task.id;
	const liveUrl = publicUrlFor(task.environment, task.url);
	const canFix = Boolean(task.sourceContentId);

	return (
		<article
			className={`group rounded-2xl border bg-white p-4 shadow-sm transition hover:shadow-md ${
				accent === "rose" ? "border-rose-100 hover:border-rose-200" : "border-amber-100 hover:border-amber-200"
			} ${isOpen ? "" : "opacity-75"}`}
		>
			<div className="flex items-start gap-3">
				<SeverityDot severity={task.severity} />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<a
							href={liveUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex min-w-0 items-center gap-1.5 font-mono text-sm font-semibold text-slate-900 transition hover:text-sky-700 hover:underline"
							title={`Ouvrir ${liveUrl} (nouvel onglet)`}
						>
							<span className="max-w-[420px] truncate">{displayPath(task.url)}</span>
							<FiExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" />
						</a>
						<span
							className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
								task.severity === "error" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"
							}`}
						>
							{taskKindLabel(task.kind)}
						</span>
						<TaskStatusPill status={task.status} />
						<span className="ml-auto shrink-0 text-[11px] text-slate-400" title={formatTaskDate(task.createdAt)}>
							{formatTaskDate(task.createdAt)}
						</span>
					</div>

					{(task.sourceTitle || task.sourceSchemaKey) && (
						<p className="mt-1 truncate text-xs text-slate-500" title={task.sourceTitle ?? undefined}>
							Contenu retiré : <span className="font-semibold text-slate-600">{task.sourceTitle || task.sourceContentId}</span>
							{task.sourceSchemaKey && (
								<span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
									{task.sourceSchemaKey}
								</span>
							)}
						</p>
					)}

					{(details.staticLinks.length > 0 || details.targetingRedirects.length > 0) && (
						<div className="mt-2.5 space-y-2 border-t border-slate-100 pt-2.5">
							{details.staticLinks.length > 0 && (
								<div>
									<p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
										<FiLink2 className="h-3 w-3" /> Liens entrants ({details.staticLinks.length})
									</p>
									<ul className="mt-1 flex flex-wrap gap-1.5">
										{details.staticLinks.map((link, index) => {
											const route = editorRouteFor(link.schemaKey, link.contentId);
											const label = link.title?.trim() || link.contentId || "Contenu inconnu";
											const body = (
												<>
													<span className="max-w-[240px] truncate font-semibold">{label}</span>
													{link.linkLabel && (
														<span className="max-w-[160px] truncate text-slate-400">« {link.linkLabel} »</span>
													)}
												</>
											);
											return route ? (
												<Link
													key={`task-link-${task.id}-${index}`}
													to={route}
													className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
													title={`Ouvrir « ${label} » dans l'éditeur${link.linkPath ? ` : ${link.linkPath}` : ""}`}
												>
													{body}
												</Link>
											) : (
												<span
													key={`task-link-${task.id}-${index}`}
													className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
													title={link.linkPath ?? undefined}
												>
													{body}
												</span>
											);
										})}
									</ul>
								</div>
							)}
							{details.targetingRedirects.length > 0 && (
								<div>
									<p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
										<FiCornerUpRight className="h-3 w-3" /> Redirections concernées ({details.targetingRedirects.length})
									</p>
									<ul className="mt-1 space-y-1">
										{details.targetingRedirects.map((rule, index) => (
											<li
												key={`task-rule-${task.id}-${index}`}
												className="truncate font-mono text-xs text-slate-600"
												title={`${displayPath(rule.fromPath)} → ${displayPath(rule.toPath)}`}
											>
												{displayPath(rule.fromPath)} <span className="text-slate-400">→</span> {displayPath(rule.toPath)}
											</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					{/* Toutes les mutations de tâche exigent feature:redirections:write côté serveur (403). */}
					<div className="mt-3 flex flex-wrap items-center gap-2">
						{isOpen ? (
							<>
								<GatedButton
									token="feature:redirections:write"
									onClick={() => onFix(task)}
									disabled={isMutating || !canFix}
									title={
										canFix
											? "Choisir la page de remplacement et réécrire les liens"
											: "Cette tâche n'a pas de contenu source : créez plutôt une redirection."
									}
									className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
								>
									<FiTool className="h-3.5 w-3.5" /> Corriger…
								</GatedButton>
								<GatedButton
									token="feature:redirections:write"
									onClick={() => onCreateRedirect(task)}
									disabled={isMutating}
									className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
								>
									<FiCornerUpRight className="h-3.5 w-3.5" /> Créer une redirection
								</GatedButton>
								<GatedButton
									token="feature:redirections:write"
									onClick={() => onDismiss(task)}
									disabled={isMutating}
									className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isMutating ? <LoadingSpinner size="xs" inline /> : <FiEyeOff className="h-3.5 w-3.5" />} Ignorer
								</GatedButton>
							</>
						) : (
							<GatedButton
								token="feature:redirections:write"
								onClick={() => onReopen(task)}
								disabled={isMutating}
								className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{isMutating ? <LoadingSpinner size="xs" inline /> : <FiRotateCcw className="h-3.5 w-3.5" />} Rouvrir
							</GatedButton>
						)}
					</div>
				</div>
			</div>
		</article>
	);
};

// ---------------------------------------------------------------------------
// Le panneau d'un environnement (Préview / Production)
// ---------------------------------------------------------------------------

const TaskListPanel: React.FC<{
	environment: "preview" | "production";
	accent: Accent;
	/** Rafraîchit les compteurs des onglets après toute mutation. */
	onChanged: () => void;
}> = ({ environment, accent, onChanged }) => {
	const [status, setStatus] = useState<StatusFilter>("open");
	const [items, setItems] = useState<RedirectTask[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [mutatingId, setMutatingId] = useState<string | null>(null);
	const [fixTarget, setFixTarget] = useState<{ target: FixSourceTarget; task: RedirectTask } | null>(null);
	const [redirectTask, setRedirectTask] = useState<RedirectTask | null>(null);
	const [showClearTasks, setShowClearTasks] = useState(false);
	const [clearingTasks, setClearingTasks] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const response = await fetchTasks(environment, status, page, TASK_PAGE_SIZE);
			setItems(response.items);
			setTotal(response.total);
		} catch (err) {
			console.error("Échec du chargement des tâches", err);
			setItems([]);
			setTotal(0);
			setLoadError(extractErrorMessage(err) ?? "Impossible de charger les tâches.");
		} finally {
			setLoading(false);
		}
	}, [environment, status, page]);

	useEffect(() => {
		load();
	}, [load]);

	const refreshAll = useCallback(() => {
		load();
		onChanged();
	}, [load, onChanged]);

	const handleDismiss = async (task: RedirectTask) => {
		if (!window.confirm("Ignorer cette tâche ? Elle n'apparaîtra plus parmi les tâches ouvertes.")) return;
		setMutatingId(task.id);
		try {
			await dismissTask(task.id);
			toast.success("Tâche ignorée.");
			refreshAll();
		} catch (err) {
			console.error("Échec de l'action Ignorer", err);
			toast.error(extractErrorMessage(err) ?? "Impossible d'ignorer la tâche.");
		} finally {
			setMutatingId(null);
		}
	};

	const handleReopen = async (task: RedirectTask) => {
		setMutatingId(task.id);
		try {
			await reopenTask(task.id);
			toast.success("Tâche rouverte.");
			refreshAll();
		} catch (err) {
			console.error("Échec de la réouverture", err);
			toast.error(extractErrorMessage(err) ?? "Impossible de rouvrir la tâche.");
		} finally {
			setMutatingId(null);
		}
	};

	const handleClearAllTasks = async () => {
		setClearingTasks(true);
		try {
			const deleted = await clearAllTasks();
			setShowClearTasks(false);
			toast.success(
				deleted > 0
					? `${deleted} tâche${deleted > 1 ? "s" : ""} supprimée${deleted > 1 ? "s" : ""} et notifications retirées.`
					: "Aucune tâche à supprimer."
			);
			setPage(1);
			refreshAll();
		} catch (err) {
			console.error("Échec de la purge des tâches", err);
			toast.error(extractErrorMessage(err) ?? "Impossible de vider les tâches.");
		} finally {
			setClearingTasks(false);
		}
	};

	const totalPages = Math.max(1, Math.ceil(total / TASK_PAGE_SIZE));

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
					{STATUS_FILTERS.map((filter) => (
						<button
							key={filter.key}
							type="button"
							onClick={() => {
								setStatus(filter.key);
								setPage(1);
							}}
							className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
								status === filter.key ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
							}`}
						>
							{filter.label}
						</button>
					))}
				</div>
				<span className="text-sm text-slate-500">
					{total.toLocaleString("fr-FR")} tâche{total > 1 ? "s" : ""}
				</span>
				<GatedButton
					token="feature:redirections:write"
					onClick={() => setShowClearTasks(true)}
					disabled={clearingTasks}
					title="Supprime TOUTES les tâches (tous statuts, préview et production) et retire leurs notifications."
					className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiTrash2 className="h-4 w-4" /> Vider toutes les tâches
				</GatedButton>
				<button
					type="button"
					onClick={refreshAll}
					className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 shadow-sm transition hover:bg-slate-50"
					title="Actualiser"
				>
					<FiRefreshCw className="h-4 w-4" />
				</button>
			</div>

			{loadError && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{loadError}</p>
			)}

			{loading ? (
				<div className="space-y-3">
					{[0, 1, 2].map((row) => (
						<div key={`task-skel-${row}`} className="h-28 animate-pulse rounded-2xl border border-slate-100 bg-slate-100" />
					))}
				</div>
			) : items.length ? (
				<div className="space-y-3">
					{items.map((task) => (
						<TaskCard
							key={task.id}
							task={task}
							accent={accent}
							mutatingId={mutatingId}
							onFix={(selected) =>
								setFixTarget({ target: { taskId: selected.id }, task: selected })
							}
							onCreateRedirect={setRedirectTask}
							onDismiss={handleDismiss}
							onReopen={handleReopen}
						/>
					))}
				</div>
			) : (
				<div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
					{status === "open" ? (
						<>
							<FiCheckCircle className="mx-auto h-8 w-8 text-emerald-500" />
							<p className="mt-2 text-sm font-semibold text-slate-700">
								Aucune tâche ouverte : tous les liens sont cohérents.
							</p>
							<p className="mt-1 text-xs text-slate-400">
								Les tâches apparaissent ici quand le retrait d'un contenu casse un lien entrant ou une redirection.
							</p>
						</>
					) : (
						<p className="text-sm text-slate-400">Aucune tâche dans ce filtre.</p>
					)}
				</div>
			)}

			{totalPages > 1 && (
				<div className="flex items-center justify-end gap-1 text-sm text-slate-600">
					<button
						type="button"
						disabled={page <= 1 || loading}
						onClick={() => setPage((current) => Math.max(1, current - 1))}
						className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						title="Page précédente"
					>
						<FiChevronLeft />
					</button>
					<span className="px-1.5">
						Page {page} / {totalPages}
					</span>
					<button
						type="button"
						disabled={page >= totalPages || loading}
						onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
						className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
						title="Page suivante"
					>
						<FiChevronRight />
					</button>
				</div>
			)}

			<FixSourceModal
				show={Boolean(fixTarget)}
				onHide={() => setFixTarget(null)}
				target={fixTarget?.target ?? null}
				contextUrl={fixTarget?.task.url}
				excludeContentId={fixTarget?.task.sourceContentId}
				onFixed={refreshAll}
			/>
			<CreateRedirectFromTaskModal task={redirectTask} onHide={() => setRedirectTask(null)} onCreated={refreshAll} />
			<PurgeConfirmOverlay
				show={showClearTasks}
				title="Vider toutes les tâches"
				description={
					<p>
						Cette action supprime <span className="font-semibold">définitivement toutes les tâches</span> de
						redirection (préview ET production, tous statuts) et retire les notifications associées. Les règles
						de redirection ne sont pas touchées.
					</p>
				}
				busy={clearingTasks}
				onCancel={() => setShowClearTasks(false)}
				onConfirm={handleClearAllTasks}
			/>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Le panneau « Toutes les redirections » (règles)
// ---------------------------------------------------------------------------

const RulesPanel: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
	const [items, setItems] = useState<Redirect[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	const [fromPath, setFromPath] = useState("");
	const [toPath, setToPath] = useState("");
	const [isPermanent, setIsPermanent] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	// Bandeau inline persistant sous le formulaire : le refus anti-boucle (400) doit rester
	// lisible le temps de corriger la saisie, pas seulement le temps d'un toast.
	const [addError, setAddError] = useState<string | null>(null);

	const [publishing, setPublishing] = useState<"preview" | "production" | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [fixingId, setFixingId] = useState<string | null>(null);

	// Recherche débouncée : pas une requête par frappe.
	useEffect(() => {
		const handle = window.setTimeout(() => {
			setSearch(searchInput.trim());
		}, 300);
		return () => window.clearTimeout(handle);
	}, [searchInput]);

	const loadRedirects = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const response = await fetchRedirects(search || undefined, 1, 100);
			setItems(response.items);
			setTotal(response.total);
		} catch (err) {
			console.error("Échec du chargement des redirections", err);
			setItems([]);
			setTotal(0);
			setLoadError(extractErrorMessage(err) ?? "Impossible de charger les redirections.");
		} finally {
			setLoading(false);
		}
	}, [search]);

	useEffect(() => {
		loadRedirects();
	}, [loadRedirects]);

	const handleAddRedirect = async (event: React.FormEvent) => {
		event.preventDefault();
		const from = fromPath.trim();
		const to = toPath.trim();
		if (!from || !to) {
			setAddError("Les champs « Depuis » et « Vers » sont obligatoires.");
			return;
		}
		setSubmitting(true);
		setAddError(null);
		try {
			await createRedirect(from, to, isPermanent);
			toast.success("Redirection ajoutée et publiée sur les deux cibles.");
			setFromPath("");
			setToPath("");
			setIsPermanent(true);
			await loadRedirects();
			onChanged(); // une nouvelle règle peut résoudre des tâches → compteurs à jour
		} catch (err) {
			console.error("Échec de l'ajout de la redirection", err);
			// Le 400 anti-boucle porte un message français corrigeable par l'utilisateur :
			// affiché en bandeau persistant sous le formulaire, en plus du toast fugace.
			const message = extractErrorMessage(err) ?? "Impossible d'ajouter la redirection.";
			setAddError(message);
			toast.error(message);
		} finally {
			setSubmitting(false);
		}
	};

	// AUCUN bouton de suppression globale des RÈGLES ici, volontairement : les règles
	// sont un actif (liens externes, forums) ; seule la file des TÂCHES se vide en masse.

	const handleDelete = async (id: string) => {
		if (!window.confirm("Supprimer cette redirection ? Elle cesse d'être servie immédiatement et l'ancienne URL répondra en 404.")) return;
		setDeletingId(id);
		try {
			await deleteRedirect(id);
			toast.success("Redirection supprimée et retirée des deux cibles.");
			await loadRedirects();
			onChanged();
		} catch (err) {
			console.error("Échec de la suppression de la redirection", err);
			toast.error(extractErrorMessage(err) ?? "Impossible de supprimer la redirection.");
		} finally {
			setDeletingId(null);
		}
	};

	const handleFixLinks = async (redirect: Redirect) => {
		setFixingId(redirect.id);
		try {
			const result = await fixRuleLinks(redirect.id);
			const count = result.rewrittenContentIds?.length ?? 0;
			toast.success(
				count > 0
					? `${count} contenu${count > 1 ? "s" : ""} réécrit${count > 1 ? "s" : ""} : les liens pointent désormais directement vers la cible.`
					: "Aucun contenu ne dépendait de cette redirection."
			);
			onChanged();
		} catch (err) {
			console.error("Échec de la correction des liens", err);
			// 400 = cible externe : les liens internes doivent être réécrits manuellement.
			toast.error(extractErrorMessage(err) ?? "Impossible de corriger les liens de cette redirection.");
		} finally {
			setFixingId(null);
		}
	};

	const handlePublish = async (production: boolean) => {
		setPublishing(production ? "production" : "preview");
		try {
			await publishRedirects(production);
			toast.success(`redirects.json publié (${production ? "production" : "préview"}).`);
		} catch (err) {
			console.error("Échec de la publication des redirections", err);
			toast.error(extractErrorMessage(err) ?? "Impossible de publier les redirections.");
		} finally {
			setPublishing(null);
		}
	};

	return (
		<section className="space-y-6">
			<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ajouter une redirection</p>
						<p className="mt-1 text-sm text-slate-500">
							La règle est publiée automatiquement sur les deux cibles dès l'ajout ; les boutons de
							publication restent en secours.
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<GatedButton
							token="feature:redirections:write"
							onClick={() => handlePublish(false)}
							disabled={publishing !== null}
							title="Écrit redirects.json pour le site de préview."
							className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{publishing === "preview" ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-4 w-4" />}
							{publishing === "preview" ? "Publication…" : "Publier (préview)"}
						</GatedButton>
						<GatedButton
							token="feature:redirections:write"
							onClick={() => handlePublish(true)}
							disabled={publishing !== null}
							title="Écrit redirects.json pour le site de production."
							className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{publishing === "production" ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-4 w-4" />}
							{publishing === "production" ? "Publication…" : "Publier (production)"}
						</GatedButton>
					</div>
				</div>
				<form onSubmit={handleAddRedirect} className="mt-4 flex flex-wrap items-end gap-4">
					<label className="flex flex-1 flex-col gap-1 sm:min-w-[220px]">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Depuis</span>
						<input
							type="text"
							value={fromPath}
							onChange={(e) => setFromPath(e.target.value)}
							placeholder="/ancienne-url"
							className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					</label>
					<label className="flex flex-1 flex-col gap-1 sm:min-w-[220px]">
						<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vers</span>
						<input
							type="text"
							value={toPath}
							onChange={(e) => setToPath(e.target.value)}
							placeholder="/nouvelle-url"
							className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					</label>
					<label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
						<input
							type="checkbox"
							checked={isPermanent}
							onChange={(e) => setIsPermanent(e.target.checked)}
							className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
						/>
						Permanente (301)
					</label>
					{/* Refusé → type="button" (GatedButton) : le formulaire ne se soumet pas, la modale explique. */}
					<GatedButton
						token="feature:redirections:write"
						type="submit"
						disabled={submitting}
						className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{submitting ? "Ajout…" : "Ajouter"}
					</GatedButton>
				</form>
				{addError && (
					<p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
						{addError}
					</p>
				)}
			</div>

			<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
				<div className="mb-4 flex flex-wrap items-center gap-3">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Redirections</p>
						<p className="mt-1 text-sm text-slate-500">
							{total.toLocaleString("fr-FR")} règle{total > 1 ? "s" : ""}
							{search ? ` correspondant à « ${search} »` : ""}
						</p>
					</div>
					<div className="ml-auto flex flex-1 items-center sm:max-w-sm">
						<input
							type="search"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							placeholder="Rechercher une redirection…"
							className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:bg-white focus:outline-none"
						/>
					</div>
				</div>

				{loadError && (
					<p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
						{loadError}
					</p>
				)}

				{loading && !items.length ? (
					<div className="space-y-2">
						{[0, 1, 2, 3].map((row) => (
							<div key={`rule-skel-${row}`} className="h-12 animate-pulse rounded-lg bg-slate-100" />
						))}
					</div>
				) : items.length ? (
					<div className="overflow-x-auto">
						{loading && <p className="mb-2 text-xs text-slate-500">Actualisation…</p>}
						<table className="min-w-full divide-y divide-slate-200 text-sm">
							<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
								<tr>
									<th scope="col" className="px-4 py-3 text-left">
										Depuis → Vers
									</th>
									<th scope="col" className="px-4 py-3 text-left">
										Type
									</th>
									<th scope="col" className="px-4 py-3 text-left">
										Source
									</th>
									<th scope="col" className="px-4 py-3 text-left">
										Passages
									</th>
									<th scope="col" className="px-4 py-3 text-left">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{items.map((redirect) => (
									<tr key={redirect.id} className="align-top transition hover:bg-slate-50/60">
										<td className="px-4 py-3">
											<p className="max-w-[380px] truncate font-mono text-xs font-semibold text-slate-900" title={redirect.fromPath}>
												{redirect.fromPath}
											</p>
											<p className="max-w-[380px] truncate font-mono text-xs text-slate-500" title={redirect.toPath}>
												→ {redirect.toPath}
											</p>
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
													redirect.isPermanent
														? "bg-emerald-100 text-emerald-700"
														: "bg-amber-100 text-amber-700"
												}`}
											>
												{redirect.isPermanent ? "301" : "302"}
											</span>
										</td>
										<td className="px-4 py-3 text-xs text-slate-600">{redirect.source || "-"}</td>
										<td className="px-4 py-3 text-slate-700">{redirect.hits}</td>
										<td className="px-4 py-3">
											<div className="flex flex-wrap items-center gap-1.5">
												<GatedButton
													token="feature:redirections:write"
													onClick={() => handleFixLinks(redirect)}
													disabled={fixingId === redirect.id}
													title="Réécrit les liens internes qui passent encore par cette règle pour pointer directement la cible."
													className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													{fixingId === redirect.id ? <LoadingSpinner size="xs" inline /> : <FiTool className="h-3 w-3" />}
													{fixingId === redirect.id ? "Correction…" : "Corriger les liens"}
												</GatedButton>
												<GatedButton
													token="feature:redirections:write"
													onClick={() => handleDelete(redirect.id)}
													disabled={deletingId === redirect.id}
													className="rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													{deletingId === redirect.id ? "Suppression…" : "Supprimer"}
												</GatedButton>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<p className="text-sm text-slate-500">
						{search ? "Aucune redirection ne correspond à votre recherche." : "Aucune redirection définie pour le moment."}
					</p>
				)}
			</div>
		</section>
	);
};

// ---------------------------------------------------------------------------
// La page : 3 onglets : Préview / Production / Toutes les redirections
// ---------------------------------------------------------------------------

/**
 * Redirections + cohérence des liens. Deux onglets de tâches (Préview en ambre, Production en
 * rouge : la casse visible par le public est LA priorité) listent ce que le retrait de contenus
 * a cassé, avec correction à la source, création de redirection, ou mise de côté. Le troisième
 * onglet gère les règles de redirection elles-mêmes (création, recherche, publication).
 */
const Redirections: React.FC = () => {
	const [activeTab, setActiveTab] = useState<TabKey>("production");
	const [counts, setCounts] = useState<RedirectTaskCount>({ production: 0, preview: 0 });

	const loadCounts = useCallback(() => {
		fetchTaskCount()
			.then(setCounts)
			.catch(() => {
				/* on garde les derniers compteurs connus */
			});
	}, []);

	useEffect(() => {
		loadCounts();
	}, [loadCounts]);

	const tabButtonClass = (tab: TabKey) =>
		`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
			activeTab === tab
				? "bg-slate-900 text-white shadow-md"
				: "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
		}`;

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-6">
				<div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div className="flex flex-wrap items-center gap-3">
						<div>
							<p className="text-xs uppercase tracking-widest text-slate-500">Redirections</p>
							<h1 className="text-2xl font-semibold text-slate-900">Cohérence des liens</h1>
							<p className="text-sm text-slate-500">
								Suivez les liens cassés par les retraits de contenu et gérez les règles de redirection du site public.
							</p>
						</div>
						{counts.production > 0 && (
							<span className="ml-auto inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700">
								<FiAlertTriangle className="h-4 w-4" />
								{counts.production} erreur{counts.production > 1 ? "s" : ""} en production
							</span>
						)}
					</div>
					<div className="mt-4 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-3">
						<button type="button" onClick={() => setActiveTab("preview")} className={tabButtonClass("preview")}>
							Préview
							<span
								className={`inline-flex min-w-[1.4rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
									counts.preview > 0 ? "bg-amber-400 text-amber-950" : "bg-slate-200 text-slate-600"
								}`}
							>
								{counts.preview}
							</span>
						</button>
						<button type="button" onClick={() => setActiveTab("production")} className={tabButtonClass("production")}>
							Production
							<span
								className={`inline-flex min-w-[1.4rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
									counts.production > 0 ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-600"
								}`}
							>
								{counts.production}
							</span>
						</button>
						<button type="button" onClick={() => setActiveTab("rules")} className={tabButtonClass("rules")}>
							Toutes les redirections
						</button>
					</div>
				</div>

				{activeTab === "preview" && <TaskListPanel environment="preview" accent="amber" onChanged={loadCounts} />}
				{activeTab === "production" && <TaskListPanel environment="production" accent="rose" onChanged={loadCounts} />}
				{activeTab === "rules" && <RulesPanel onChanged={loadCounts} />}
			</div>
		</div>
	);
};

export default Redirections;
